// AR / AP aging math (Phase C). Standard 30/60/90 buckets every CFO
// already knows. One helper here means the AR view on InvoicesPage and
// the AP view on POsPage share the exact same math — no "why does the
// dashboard tile say $4,200 past due but the report says $4,180" drift.

const DAY_MS = 86400000;

// Default payment terms when nothing else is set. Most commercial work
// in the US is Net 30; org-configurable later (project_org_billing_defaults).
export const DEFAULT_AR_TERMS_DAYS = 30;

// Bucket labels in render order. Each row of the aging table goes
// through this list in this order so the colors + columns align between
// AR and AP views.
export const AGING_BUCKETS = [
  { key: "current", label: "Current", color: "#1a7f37", bg: "#d6f5dd" },
  {
    key: "d1_30",
    label: "1-30 days",
    color: "#92400e",
    bg: "#fff3cd",
  },
  { key: "d31_60", label: "31-60 days", color: "#c2410c", bg: "#ffe4c8" },
  { key: "d61_90", label: "61-90 days", color: "#b91c1c", bg: "#fee2e2" },
  { key: "d90_plus", label: "90+ days", color: "#7f1d1d", bg: "#fbb4b4" },
];

// Categorize a single days-past-due value into one of the bucket keys.
// Negative or zero days = current (still within terms).
export const bucketForDays = (daysPastDue) => {
  if (!Number.isFinite(daysPastDue) || daysPastDue <= 0) return "current";
  if (daysPastDue <= 30) return "d1_30";
  if (daysPastDue <= 60) return "d31_60";
  if (daysPastDue <= 90) return "d61_90";
  return "d90_plus";
};

// Days past due for a bill, given today. Parses YYYY-MM-DD or ISO
// timestamps; returns null when the date is unparseable.
export const daysPastDue = (dueDate, today = new Date()) => {
  if (!dueDate) return null;
  let dt;
  if (typeof dueDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    const [y, m, d] = dueDate.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return null;
    dt = new Date(y, m - 1, d);
  } else {
    dt = new Date(dueDate);
  }
  if (Number.isNaN(dt.getTime())) return null;
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);
  dt.setHours(0, 0, 0, 0);
  return Math.round((t.getTime() - dt.getTime()) / DAY_MS);
};

// Resolve a bill's effective due date. For modern bills generated post-
// Phase C, dueDate is stamped at generate time. For older bills that
// pre-date the dueDate stamp, fall back to finalizedAt + N days so the
// aging math still produces something useful instead of bailing out.
export const effectiveDueDate = (bill, defaultTermsDays = DEFAULT_AR_TERMS_DAYS) => {
  if (bill?.dueDate) return bill.dueDate;
  if (!bill?.finalizedAt) return null;
  const f = new Date(bill.finalizedAt);
  if (Number.isNaN(f.getTime())) return null;
  f.setDate(f.getDate() + defaultTermsDays);
  return f.toISOString().slice(0, 10);
};

// Reduce a flat list of bills (with .grandTotal, .paidAmount, .dueDate)
// into the standard aging summary: total per bucket + count per bucket
// + grand total outstanding. The caller filters out voided / fully paid
// bills before passing them in.
export const summarizeAging = (bills, today = new Date(), defaultTermsDays = DEFAULT_AR_TERMS_DAYS) => {
  const empty = AGING_BUCKETS.reduce(
    (acc, b) => ({ ...acc, [b.key]: { amount: 0, count: 0 } }),
    {}
  );
  let totalOutstanding = 0;
  for (const bill of bills || []) {
    const grand = parseFloat(bill.grandTotal) || 0;
    const paid = parseFloat(bill.paidAmount) || 0;
    const remaining = Math.max(0, grand - paid);
    if (remaining < 0.005) continue;
    const due = effectiveDueDate(bill, defaultTermsDays);
    const days = daysPastDue(due, today);
    const key = bucketForDays(days);
    empty[key].amount += remaining;
    empty[key].count += 1;
    totalOutstanding += remaining;
  }
  return { buckets: empty, totalOutstanding };
};

// Per-customer payment-history rollup. Returns array sorted by total
// paid descending — top customers first. Each entry includes paid YTD,
// outstanding balance, lifetime billed, and the count of invoices that
// were paid late. UI renders this as the customer payment-history view.
export const summarizeByCustomer = (
  bills,
  customerNameLookup,
  today = new Date(),
  defaultTermsDays = DEFAULT_AR_TERMS_DAYS
) => {
  const byCust = new Map();
  const yearStart = new Date(today.getFullYear(), 0, 1);
  for (const bill of bills || []) {
    const cust = bill.customerId || "";
    if (!byCust.has(cust)) {
      byCust.set(cust, {
        customerId: cust,
        customerName:
          (customerNameLookup && customerNameLookup(cust)) || "(unknown)",
        totalBilled: 0,
        totalPaid: 0,
        paidYtd: 0,
        outstanding: 0,
        billsCount: 0,
        paidLateCount: 0,
        lastPaymentAt: null,
      });
    }
    const row = byCust.get(cust);
    const grand = parseFloat(bill.grandTotal) || 0;
    const paid = parseFloat(bill.paidAmount) || 0;
    row.totalBilled += grand;
    row.totalPaid += paid;
    row.outstanding += Math.max(0, grand - paid);
    row.billsCount += 1;
    for (const p of bill.payments || []) {
      const amt = parseFloat(p.amount) || 0;
      const pDate = p.date ? new Date(p.date) : null;
      if (pDate && !Number.isNaN(pDate.getTime()) && pDate >= yearStart) {
        row.paidYtd += amt;
      }
      const stamp = p.recordedAt || p.date || null;
      if (
        stamp &&
        (!row.lastPaymentAt || new Date(stamp) > new Date(row.lastPaymentAt))
      ) {
        row.lastPaymentAt = stamp;
      }
    }
    // "Paid late" = the bill is fully paid AND its last payment landed
    // after the due date.
    if (
      bill.paymentStatus === "paid" &&
      (bill.payments || []).length > 0
    ) {
      const due = effectiveDueDate(bill, defaultTermsDays);
      const last = bill.lastPaymentAt;
      if (due && last && new Date(last) > new Date(due)) {
        row.paidLateCount += 1;
      }
    }
  }
  return [...byCust.values()].sort((a, b) => b.totalPaid - a.totalPaid);
};
